/**
 * Global Error Boundary Component with comprehensive error handling and recovery
 */

import React, { Component, ReactNode } from 'react';
import type { ErrorInfo } from 'react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Home, RotateCcw } from 'lucide-react';
import { 
  ErrorBoundaryState, 
  ErrorBoundaryProps, 
  ErrorFallbackProps,
  ErrorDetails,
  ErrorRecoveryAction,
  ErrorCategory,
  ErrorSeverity
} from '../types/error';
import { errorLogger } from '../services/errorLogger';
import { 
  createErrorDetails, 
  createUserFriendlyMessage, 
  isChunkLoadError, 
  handleChunkLoadError,
  formatErrorForDisplay
} from '../utils/errorUtils';

/**
 * Default Error Fallback Component
 */
const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorDetails,
  resetError,
  retryAction,
  isRetrying,
  canRetry,
  recoveryActions = []
}) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const displayInfo = formatErrorForDisplay(errorDetails);

  const getSeverityIcon = (severity: ErrorSeverity) => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case ErrorSeverity.HIGH:
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case ErrorSeverity.MEDIUM:
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-blue-600" />;
    }
  };

  const getSeverityVariant = (severity: ErrorSeverity): "destructive" | "default" => {
    return severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH 
      ? "destructive" 
      : "default";
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4">
        <Alert variant={getSeverityVariant(displayInfo.severity)} className="border-2">
          <div className="flex items-start space-x-3">
            {getSeverityIcon(displayInfo.severity)}
            <div className="flex-1 space-y-3">
              <AlertTitle className="text-lg font-semibold">
                {displayInfo.title}
              </AlertTitle>
              <AlertDescription className="text-sm leading-relaxed">
                {displayInfo.message}
              </AlertDescription>

              {/* Recovery Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {canRetry && (
                  <Button
                    onClick={retryAction}
                    disabled={isRetrying}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {isRetrying ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    {isRetrying ? 'Retrying...' : 'Try Again'}
                  </Button>
                )}
                
                <Button
                  onClick={resetError}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </Button>

                <Button
                  onClick={() => window.location.href = '/'}
                  size="sm"
                  variant="ghost"
                  className="flex items-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Go Home
                </Button>

                {/* Custom recovery actions */}
                {recoveryActions.map((action) => (
                  <Button
                    key={action.id}
                    onClick={action.action}
                    disabled={action.disabled}
                    size="sm"
                    variant={action.variant || "outline"}
                    className="flex items-center gap-2"
                  >
                    {action.label}
                  </Button>
                ))}
              </div>

              {/* Error Details Toggle */}
              <div className="pt-2 border-t border-border/50">
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {showDetails ? (
                    <>
                      <ChevronUp className="h-3 w-3 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Show Details
                    </>
                  )}
                </Button>

                {showDetails && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <div>
                        <strong>Error ID:</strong> {errorDetails.id}
                      </div>
                      <div>
                        <strong>Category:</strong> {errorDetails.category}
                      </div>
                      <div>
                        <strong>Severity:</strong> {errorDetails.severity}
                      </div>
                      <div>
                        <strong>Time:</strong> {new Date(displayInfo.timestamp).toLocaleString()}
                      </div>
                      {errorDetails.retryCount && (
                        <div>
                          <strong>Retry Count:</strong> {errorDetails.retryCount}
                        </div>
                      )}
                      {error.stack && (
                        <div className="mt-2">
                          <strong>Stack Trace:</strong>
                          <pre className="mt-1 text-xs bg-background p-2 rounded overflow-x-auto">
                            {error.stack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Alert>

        {/* Additional Help Text */}
        <div className="text-center text-xs text-muted-foreground">
          If the problem persists, please contact support with Error ID: {errorDetails.id}
        </div>
      </div>
    </div>
  );
};

/**
 * Main Error Boundary Class Component
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorDetails: null,
      errorId: null,
      retryCount: 0,
      isRetrying: false,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Handle chunk loading errors immediately
    if (isChunkLoadError(error)) {
      handleChunkLoadError();
      return {};
    }

    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorDetails = this.createErrorDetailsFromError(error, errorInfo);
    
    // Log the error
    errorLogger.logError(errorDetails);

    // Update state with error details
    this.setState({
      errorDetails,
      errorId: errorDetails.id,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, errorDetails);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset on props change if enabled
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetError();
    }

    // Reset on resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys !== resetKeys) {
      this.resetError();
    }
  }

  private createErrorDetailsFromError(error: Error, errorInfo: React.ErrorInfo): ErrorDetails {
    const errorDetails = createErrorDetails(error);
    
    // Add React error info with proper typing
    errorDetails.errorInfo = {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.constructor.name,
      errorBoundaryStack: ''
    };
    errorDetails.maxRetries = this.props.maxRetries || 3;
    
    // Add boundary-specific context
    errorDetails.context = {
      ...errorDetails.context,
      level: this.props.level || 'component',
    };

    return errorDetails;
  }

  private resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorDetails: null,
      errorId: null,
      retryCount: 0,
      isRetrying: false,
      showDetails: false,
    });
  };

  private retryAction = async (): Promise<void> => {
    const { errorDetails, retryCount } = this.state;
    const maxRetries = this.props.maxRetries || 3;

    if (!errorDetails || !errorDetails.recoverable || retryCount >= maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    // Wait before retry
    await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));

    this.setState({
      retryCount: retryCount + 1,
      isRetrying: false,
    });

    // Reset the error boundary
    this.resetError();
  };

  private getRecoveryActions(): ErrorRecoveryAction[] {
    const actions: ErrorRecoveryAction[] = [];

    // Add reload page action for critical errors
    if (this.state.errorDetails?.severity === ErrorSeverity.CRITICAL) {
      actions.push({
        id: 'reload',
        label: 'Reload Page',
        action: () => window.location.reload(),
        variant: 'default',
      });
    }

    // Add clear cache action for network errors
    if (this.state.errorDetails?.category === ErrorCategory.NETWORK) {
      actions.push({
        id: 'clear-cache',
        label: 'Clear Cache',
        action: async () => {
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          }
          localStorage.clear();
          sessionStorage.clear();
          window.location.reload();
        },
        variant: 'outline',
      });
    }

    return actions;
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorDetails) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      const canRetry = this.state.errorDetails.recoverable && 
                      this.state.retryCount < (this.props.maxRetries || 3);

      return (
        <FallbackComponent
          error={this.state.error}
          errorDetails={this.state.errorDetails}
          resetError={this.resetError}
          retryAction={this.retryAction}
          isRetrying={this.state.isRetrying}
          canRetry={canRetry}
          recoveryActions={this.getRecoveryActions()}
        />
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
export default ErrorBoundary;